import { useEffect, useState, useRef } from "react";
import { useParams, Link, useSearchParams, useLocation } from "react-router-dom";
import { api } from "../api/client.js";

async function batchAssetUrls(assetIds: string[]): Promise<Record<string, string>> {
  const ids = [...new Set(assetIds.filter(Boolean))];
  if (ids.length === 0) return {};
  try {
    const r = await api.post<{ urls: Record<string, string> }>("/assets/batch-urls", { assetIds: ids });
    return r.urls ?? {};
  } catch { return {}; }
}

interface Shot {
  id: string; position: number; purpose: string; status: string;
  planRevision: number; diversityWarning?: string;
  selectedAssetId?: string; regenCount?: number;
  lastPrompt?: string | null; lastModel?: string | null; lastProviderConnectionId?: string | null;
}
interface Session {
  id: string; status: string; format?: string; budgetCapMicroeur?: string;
  influencerId?: string; identityVersionId?: string;
  planRevision: number; revision: number;
  providerConnectionId?: string;
  name?: string | null;
  shots: Shot[];
  createdAt: string;
  completedByPosition?: Record<number, string>; // position → selectedAssetId (any revision)
}
interface FamilyMember {
  id: string; name: string; relation: string; fictionalAge: number | null;
  traitsJson: string; referenceAssetId: string | null;
  linkedInfluencerId: string | null; notes: string | null;
}
interface Provider { id: string; kind: string; providerKey: string; displayName: string; status: string; }
interface Variant { jobId: string; state: string; }
interface IdentityVersion { id: string; status: string; traitsJson?: string; }
interface Traits { hair_color?: string; eye_color?: string; skin_tone?: string; age_appearance?: string; body_type?: string; style_keywords?: string[]; notes?: string; }

const PROVIDER_LABELS: Record<string, string> = {
  gemini: "Gemini (Google)",
  bfl: "Flux Pro (BFL)",
  flux: "Flux Pro (BFL)",
  openai: "ChatGPT / DALL-E 3",
  dalle: "ChatGPT / DALL-E 3",
  mock: "Mock (prueba)",
};

function providerLabel(p: Provider): string {
  return PROVIDER_LABELS[p.providerKey] ?? p.displayName ?? p.providerKey;
}

function buildAutoPrompt(traits: Traits, format?: string): string {
  const lines: string[] = [];
  const desc: string[] = [];
  if (traits.age_appearance) desc.push(`mujer ficticia adulta de aproximadamente ${traits.age_appearance}`);
  else desc.push("mujer ficticia adulta de aproximadamente 30-35 años");
  if (traits.hair_color) desc.push(`cabello ${traits.hair_color}`);
  if (traits.eye_color) desc.push(`ojos ${traits.eye_color}`);
  if (traits.skin_tone) desc.push(`tono de piel ${traits.skin_tone}`);
  if (traits.body_type) desc.push(`complexión ${traits.body_type}`);
  lines.push(`Genera una fotografía candid de una ${desc.join(", ")}.`);
  if (traits.style_keywords?.length) {
    lines.push(`Estilo: ${traits.style_keywords.join(", ")}.`);
  }
  lines.push("Aspecto fotográfico natural tomado con smartphone de alta gama. Textura de piel visible, imperfecciones sutiles, asimetría facial natural, iluminación ambiental creíble.");
  lines.push("Evita: piel plástica, filtros de belleza, retoque excesivo, proporciones exageradas.");
  if (format === "portrait_9_16") lines.push("Formato vertical 9:16, cuerpo entero con los pies visibles.");
  else if (format === "square_1_1") lines.push("Formato cuadrado 1:1, encuadre medio.");
  if (traits.notes) lines.push(`Notas del personaje: ${traits.notes}`);
  return lines.join("\n");
}

async function sha256hex(buf: ArrayBuffer): Promise<string> {
  const hashBuf = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(hashBuf)).map(b => b.toString(16).padStart(2, "0")).join("");
}

export function SessionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [session, setSession] = useState<Session | null>(null);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [addingShot, setAddingShot] = useState(false);
  const [prompts, setPrompts] = useState<Record<string, string>>({});
  const [shotProviders, setShotProviders] = useState<Record<string, string>>({});
  const [refImages, setRefImages] = useState<Record<string, { assetId: string; previewUrl: string; auto?: boolean; autoLabel?: string } | null>>({});
  const [uploadingRef, setUploadingRef] = useState<Record<string, boolean>>({});
  const [genState, setGenState] = useState<Record<string, { state: string; jobId?: string; assetId?: string; error?: string; fallbackNote?: string; moderationRejected?: boolean; moderationProvider?: string }>>({});
  const [assetDownloadUrls, setAssetDownloadUrls] = useState<Record<string, string>>({});
  const [shotVariants, setShotVariants] = useState<Record<string, Array<{ jobId: string; assetId: string | null; state: string; createdAt: string; editMode?: string | null }>>>({});
  const [selectingVariant, setSelectingVariant] = useState<Record<string, boolean>>({});
  const [deletingVariant, setDeletingVariant] = useState<Record<string, boolean>>({}); // jobId → deleting
  const [deletingShot, setDeletingShot] = useState<Record<string, boolean>>({}); // shotId → deleting
  const [promptBriefs, setPromptBriefs] = useState<Record<string, string>>({}); // shotId → brief idea
  const [generatingPrompt, setGeneratingPrompt] = useState<Record<string, boolean>>({}); // shotId → loading
  const [baseShotId, setBaseShotId] = useState<string | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [collapsedShots, setCollapsedShots] = useState<Record<string, boolean>>({});
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [focusNewShotId, setFocusNewShotId] = useState<string | null>(null);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [shotFamilyMember, setShotFamilyMember] = useState<Record<string, string[]>>({}); // shotId → memberIds[]
  const [influencerRefAssetId, setInfluencerRefAssetId] = useState<string | null>(null);
  const [influencerRefUrl, setInfluencerRefUrl] = useState<string | null>(null);
  const [influencerName, setInfluencerName] = useState<string | null>(null);
  const [productRefs, setProductRefs] = useState<Record<string, string>>({}); // shotId → productAssetId
  const [catalogProducts, setCatalogProducts] = useState<Array<{ id: string; title: string; category: string }>>([]);
  const [catalogProductImages, setCatalogProductImages] = useState<Record<string, { assetId: string; url: string }>>({});
  const catalogLoadedRef = useRef(false);
  const [garmentSwapMode, setGarmentSwapMode] = useState<Record<string, boolean>>({}); // shotId → boolean
  const [garmentSwapPrompts, setGarmentSwapPrompts] = useState<Record<string, string>>({}); // shotId → swap prompt
  const pollingRef = useRef<Record<string, ReturnType<typeof setInterval>>>({});
  const traitsRef = useRef<Traits>({});
  const formatRef = useRef<string | undefined>(undefined);
  const sessionRevRef = useRef<number>(0);

  // Resolve signed download URLs whenever genState gains a new assetId
  useEffect(() => {
    const missing = Object.values(genState).map(gs => gs.assetId).filter((id): id is string => !!id && !assetDownloadUrls[id]);
    if (missing.length === 0) return;
    batchAssetUrls(missing).then(urls => setAssetDownloadUrls(prev => ({ ...prev, ...urls }))).catch(() => {});
  }, [genState]);

  // Resolve download URLs for product ref assets
  useEffect(() => {
    const missing = Object.values(productRefs).filter(id => id && !assetDownloadUrls[id]);
    if (missing.length === 0) return;
    batchAssetUrls(missing).then(urls => setAssetDownloadUrls(prev => ({ ...prev, ...urls }))).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productRefs]);

  // Resolve download URLs for completedByPosition assets (used by reference picker)
  useEffect(() => {
    if (!session?.completedByPosition) return;
    const missing = Object.values(session.completedByPosition).filter(id => !assetDownloadUrls[id]);
    if (missing.length === 0) return;
    batchAssetUrls(missing).then(urls => setAssetDownloadUrls(prev => ({ ...prev, ...urls }))).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.completedByPosition]);

  async function loadVariants(shotId: string) {
    try {
      const data = await api.get<{ variants: Array<{ jobId: string; assetId: string | null; state: string; createdAt: string; editMode?: string | null }> }>(`/shots/${shotId}/variants`);
      const succeeded = data.variants.filter(v => v.state === "SUCCEEDED" && v.assetId != null);
      setShotVariants(prev => ({ ...prev, [shotId]: succeeded }));
      const variantAssetIds = succeeded.map(v => v.assetId).filter((id): id is string => !!id);
      if (variantAssetIds.length > 0) {
        batchAssetUrls(variantAssetIds).then(urls => setAssetDownloadUrls(prev => ({ ...prev, ...urls }))).catch(() => {});
      }
    } catch {
      setShotVariants(prev => ({ ...prev, [shotId]: [] }));
    }
  }

  // Retry loading variants whenever genState has a SUCCEEDED shot without variants loaded
  // Also update auto-ref for subsequent shots when a shot's selected asset changes
  useEffect(() => {
    if (!session) return;
    for (let i = 0; i < session.shots.length; i++) {
      const shot = session.shots[i]!;
      const gs = genState[shot.id];
      if (gs?.state === "SUCCEEDED" && shotVariants[shot.id] === undefined) {
        void loadVariants(shot.id);
      }
      // Auto-ref: if this shot has a selected asset, propagate to next shot
      if (gs?.assetId && i + 1 < session.shots.length) {
        const nextShot = session.shots[i + 1]!;
        const assetId = gs.assetId;
        const label = `Toma #${i + 1}`;
        const nextShotId = nextShot.id;
        const nextRef = refImages[nextShotId];
        // Update only if not manually set or if pointing to a different auto asset
        if (!nextRef || (nextRef.auto && nextRef.assetId !== assetId)) {
          if (assetDownloadUrls[assetId]) {
            setRefImages(prev => ({ ...prev, [nextShotId]: { assetId, previewUrl: assetDownloadUrls[assetId]!, auto: true, autoLabel: label } }));
          } else {
            batchAssetUrls([assetId]).then(urls => {
              if (!urls[assetId]) return;
              setRefImages(prev => {
                const cur = prev[nextShotId];
                if (cur && !cur.auto) return prev;
                return { ...prev, [nextShotId]: { assetId, previewUrl: urls[assetId]!, auto: true, autoLabel: label } };
              });
            }).catch(() => {});
          }
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genState]);

  async function selectVariant(shotId: string, jobId: string, assetId: string) {
    setSelectingVariant(prev => ({ ...prev, [shotId]: true }));
    try {
      await api.post(`/shots/${shotId}/select-variant`, { jobId });
      setGenState(prev => ({ ...prev, [shotId]: { ...prev[shotId], assetId, state: "SUCCEEDED" } }));
      setAssetDownloadUrls(prev => { const next = { ...prev }; delete next[assetId]; return next; });
      const urls = await batchAssetUrls([assetId]);
      if (urls[assetId]) setAssetDownloadUrls(prev => ({ ...prev, [assetId]: urls[assetId]! }));
    } catch { /* ignore */ } finally {
      setSelectingVariant(prev => ({ ...prev, [shotId]: false }));
    }
  }

  async function loadCatalog() {
    if (catalogLoadedRef.current) return;
    catalogLoadedRef.current = true;
    try {
      const r = await api.get<{ products: Array<{ id: string; title: string; category: string }> }>("/products?limit=100&status=active");
      const list = r.products ?? [];
      setCatalogProducts(list);
      for (const p of list) {
        api.get<{ assetRefs: Array<{ assetId: string }> }>(`/products/${p.id}`)
          .then(detail => {
            const firstRef = detail.assetRefs?.[0];
            if (!firstRef) return;
            return api.post<{ urls: Record<string, string> }>("/assets/batch-urls", { assetIds: [firstRef.assetId] })
              .then(r2 => {
                const url = r2.urls?.[firstRef.assetId];
                if (url) {
                  setCatalogProductImages(prev => ({ ...prev, [p.id]: { assetId: firstRef.assetId, url } }));
                  setAssetDownloadUrls(prev => ({ ...prev, [firstRef.assetId]: url }));
                }
              });
          })
          .catch(() => {});
      }
    } catch { catalogLoadedRef.current = false; }
  }

  async function generatePrompt(shotId: string, briefOverride?: string, productAssetIdOverride?: string | null) {
    const brief = briefOverride ?? promptBriefs[shotId]?.trim();
    if (!brief) return;
    setGeneratingPrompt(prev => ({ ...prev, [shotId]: true }));
    try {
      const body: Record<string, unknown> = { brief };
      const productAssetId = productAssetIdOverride !== undefined ? productAssetIdOverride ?? undefined : productRefs[shotId];
      if (productAssetId) body["productAssetId"] = productAssetId;
      const memberIds = shotFamilyMember[shotId] ?? [];
      const memberNames = memberIds.map(mid => familyMembers.find(m => m.id === mid)?.name).filter((n): n is string => !!n);
      if (memberNames.length > 0) body["familyMemberNames"] = memberNames;
      // Build participants for multi-character mode when family members with refs are selected
      const membersWithRef = memberIds.map(mid => familyMembers.find(m => m.id === mid)).filter((m): m is FamilyMember => !!m && !!m.referenceAssetId);
      if (membersWithRef.length > 0 && influencerRefAssetId) {
        const participants: Array<{ name: string; referenceAssetId: string }> = [
          { name: influencerName ?? "Influencer", referenceAssetId: influencerRefAssetId },
          ...membersWithRef.map(m => ({ name: m.name, referenceAssetId: m.referenceAssetId as string })),
        ];
        body["participants"] = participants;
      }
      const data = await api.post<{ prompt: string }>(`/shots/${shotId}/generate-prompt`, body);
      setPrompts(prev => ({ ...prev, [shotId]: data.prompt }));
    } catch (e) {
      const msg = (e instanceof Error) ? e.message : "Error desconocido";
      alert(`No se pudo generar el prompt: ${msg}`);
    } finally {
      setGeneratingPrompt(prev => ({ ...prev, [shotId]: false }));
    }
  }

  function applyBaseShot(shotId: string) {
    if (!session) return;
    const shot = session.shots.find(s => s.id === shotId);
    const shotIdx2 = session.shots.findIndex(s => s.id === shotId);
    const assetId = genState[shotId]?.assetId ?? shot?.selectedAssetId ?? session.completedByPosition?.[shotIdx2] ?? null;
    if (!assetId) return;
    const shotIdx = shotIdx2;
    const label = `Toma #${shotIdx + 1}`;
    setBaseShotId(shotId);
    const applyRef = (url: string) => {
      setRefImages(prev => {
        const next = { ...prev };
        for (const sh of session.shots) {
          if (sh.id === shotId) continue;
          next[sh.id] = { assetId, previewUrl: url, auto: true, autoLabel: `${label} (base)` };
        }
        return next;
      });
    };
    if (assetDownloadUrls[assetId]) {
      applyRef(assetDownloadUrls[assetId]!);
    } else {
      batchAssetUrls([assetId]).then(urls => { if (urls[assetId]) applyRef(urls[assetId]!); }).catch(() => {});
    }
  }

  async function deleteShot(shotId: string, shotLabel: string) {
    if (!window.confirm(`¿Eliminar ${shotLabel}? Se borrarán todas sus imágenes generadas permanentemente.`)) return;
    setDeletingShot(prev => ({ ...prev, [shotId]: true }));
    try {
      await api.delete(`/shots/${shotId}`);
      setSession(prev => prev ? { ...prev, shots: prev.shots.filter(s => s.id !== shotId) } : prev);
    } catch (e) {
      const msg = (e instanceof Error) ? e.message : "Error desconocido";
      alert(`No se pudo eliminar la toma: ${msg}`);
    } finally {
      setDeletingShot(prev => { const n = { ...prev }; delete n[shotId]; return n; });
    }
  }

  async function deleteVariant(shotId: string, jobId: string, assetId: string | null) {
    if (!window.confirm("¿Eliminar esta versión? Se borrará el archivo permanentemente.")) return;
    setDeletingVariant(prev => ({ ...prev, [jobId]: true }));
    try {
      await api.delete(`/shots/${shotId}/variants/${jobId}`);
      setShotVariants(prev => ({
        ...prev,
        [shotId]: (prev[shotId] ?? []).filter(v => v.jobId !== jobId),
      }));
      if (assetId) setAssetDownloadUrls(prev => { const n = { ...prev }; delete n[assetId]; return n; });
    } catch (e) {
      const msg = (e instanceof Error) ? e.message : "Error desconocido";
      alert(`No se pudo eliminar: ${msg}`);
    } finally {
      setDeletingVariant(prev => { const n = { ...prev }; delete n[jobId]; return n; });
    }
  }

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([
      api.get<Session>(`/sessions/${id}`),
      api.get<{ providers: Provider[] }>("/providers"),
    ]).then(async ([s, p]) => {
      setSession(s);
      sessionRevRef.current = s.revision;
      formatRef.current = s.format;
      const PROVIDER_ORDER: Record<string, number> = { openai: 0, dalle: 0, bfl: 1, gemini: 2, mock: 3 };
      const imageProviders = (p.providers ?? [])
        .filter(pr => pr.kind === "image" && pr.status === "active")
        .sort((a, b) => (PROVIDER_ORDER[a.providerKey] ?? 9) - (PROVIDER_ORDER[b.providerKey] ?? 9));
      setProviders(imageProviders);

      if (s.identityVersionId) {
        try {
          const iv = await api.get<IdentityVersion & { traitsJson?: string }>(`/identity-versions/${s.identityVersionId}`);
          if (iv.traitsJson) {
            try { traitsRef.current = JSON.parse(iv.traitsJson) as Traits; } catch { /* ignore */ }
          }
        } catch { /* ignore */ }
      }

      const initPrompts: Record<string, string> = {};
      const initShotProviders: Record<string, string> = {};
      const defaultProvider = imageProviders[0]?.id ?? "";
      for (const shot of s.shots) {
        initPrompts[shot.id] = shot.lastPrompt || buildAutoPrompt(traitsRef.current, s.format);
        initShotProviders[shot.id] = defaultProvider || (shot.lastProviderConnectionId ?? s.providerConnectionId ?? "");
      }
      setPrompts(initPrompts);
      setShotProviders(initShotProviders);

      const initGen: Record<string, { state: string; assetId?: string }> = {};
      for (const shot of s.shots) {
        if (shot.selectedAssetId) initGen[shot.id] = { state: "SUCCEEDED", assetId: shot.selectedAssetId };
        else if (shot.status === "failed") initGen[shot.id] = { state: "FAILED" };
      }
      setGenState(initGen);

      // All shots start collapsed
      const initCollapsed: Record<string, boolean> = {};
      for (const shot of s.shots) {
        initCollapsed[shot.id] = true;
      }

      // Pre-fill from "Crear toma desde idea" navigation state
      const navState = location.state as { shotId?: string; brief?: string; productAssetId?: string | null } | null;
      if (navState?.shotId) {
        const prefilledShot = s.shots.find(sh => sh.id === navState.shotId);
        if (prefilledShot) {
          initCollapsed[navState.shotId] = false; // expand the pre-filled shot
          // Set product ref first so it's available before generate-prompt call
          if (navState.productAssetId) {
            setProductRefs(prev => ({ ...prev, [navState.shotId!]: navState.productAssetId! }));
          }
          if (navState.brief) {
            initPrompts[navState.shotId] = ""; // clear auto-prompt so GPT generates a fresh one
            setPromptBriefs(prev => ({ ...prev, [navState.shotId!]: navState.brief! }));
            // Pass productAssetId directly to avoid React state batching timing issue
            void generatePrompt(navState.shotId, navState.brief, navState.productAssetId);
          }
        }
      }

      setCollapsedShots(initCollapsed);

      // Collect all asset IDs needed at load time and fetch in one batch
      const batchIds: string[] = [];
      for (const shot of s.shots) {
        if (shot.selectedAssetId) batchIds.push(shot.selectedAssetId);
      }
      // Previous shot refs for auto-populate
      const prevRefs: Array<{ thisShotId: string; prevAssetId: string; label: string }> = [];
      for (let i = 1; i < s.shots.length; i++) {
        const prevShot = s.shots[i - 1];
        const thisShot = s.shots[i]!;
        const prevAssetId = prevShot?.selectedAssetId ?? s.completedByPosition?.[i - 1] ?? null;
        if (prevAssetId) {
          batchIds.push(prevAssetId);
          prevRefs.push({ thisShotId: thisShot.id, prevAssetId, label: `Toma #${i}` });
        }
      }
      // Base shot from URL param (?base=INDEX)
      const baseParam = searchParams.get("base");
      let baseAssetId: string | null = null;
      let baseShot: Shot | undefined;
      if (baseParam !== null) {
        const baseIdx = parseInt(baseParam, 10);
        baseShot = s.shots[baseIdx];
        if (baseShot) {
          baseAssetId = baseShot.selectedAssetId ?? s.completedByPosition?.[baseIdx] ?? null;
          if (baseAssetId) batchIds.push(baseAssetId);
        }
      }

      batchAssetUrls(batchIds).then(urls => {
        // Set selected asset URLs
        const newUrls: Record<string, string> = {};
        for (const shot of s.shots) {
          if (shot.selectedAssetId && urls[shot.selectedAssetId]) {
            newUrls[shot.selectedAssetId] = urls[shot.selectedAssetId]!;
          }
        }
        if (Object.keys(newUrls).length > 0) setAssetDownloadUrls(prev => ({ ...prev, ...newUrls }));

        // Set base shot ref images
        if (baseAssetId && baseShot && urls[baseAssetId]) {
          const label = `Toma #${s.shots.indexOf(baseShot) + 1}`;
          setBaseShotId(baseShot.id);
          setRefImages(prev => {
            const next = { ...prev };
            for (const sh of s.shots) {
              if (sh.id === baseShot!.id) continue;
              next[sh.id] = { assetId: baseAssetId!, previewUrl: urls[baseAssetId!]!, auto: true, autoLabel: `${label} (base)` };
            }
            return next;
          });
        }

        // Auto-populate previous shot references
        setRefImages(prev => {
          const next = { ...prev };
          for (const { thisShotId, prevAssetId, label } of prevRefs) {
            if (next[thisShotId] && !next[thisShotId]?.auto) continue;
            if (urls[prevAssetId]) {
              next[thisShotId] = { assetId: prevAssetId, previewUrl: urls[prevAssetId]!, auto: true, autoLabel: label };
            }
          }
          return next;
        });
      });

      // Load variants only for expanded shots (collapsed shots defer until user opens them)
      for (const shot of s.shots) {
        if (shot.status === "completed" && !initCollapsed[shot.id]) void loadVariants(shot.id);
      }
    }).catch(() => {}).finally(() => setLoading(false));

    return () => { for (const t of Object.values(pollingRef.current)) clearInterval(t); };
  }, [id]);

  // Load family members + influencer reference photo when session loads
  useEffect(() => {
    if (!session?.influencerId) return;
    api.get<{ familyMembers: FamilyMember[] }>(`/influencers/${session.influencerId}/family-members`)
      .then(r => setFamilyMembers(r.familyMembers ?? []))
      .catch(() => {});
    api.get<{ referenceAssetId: string | null; name?: string }>(`/influencers/${session.influencerId}`)
      .then(async r => {
        const rid = r.referenceAssetId ?? null;
        setInfluencerRefAssetId(rid);
        if (r.name) setInfluencerName(r.name);
        if (rid) {
          const urls = await batchAssetUrls([rid]);
          const url = urls[rid];
          if (url) {
            setInfluencerRefUrl(url);
            setRefImages(prev => {
              const next = { ...prev };
              for (const shot of session.shots) {
                if (!next[shot.id]) {
                  next[shot.id] = { assetId: rid, previewUrl: url, auto: true, autoLabel: "Foto de perfil" };
                }
              }
              return next;
            });
          }
        }
      })
      .catch(() => {});
  }, [session?.influencerId]);

  // Load catalog products once on mount
  useEffect(() => { void loadCatalog(); }, []);

  async function addShot() {
    if (!session) return;
    setAddingShot(true);
    try {
      const existingShots = session.shots.map((s, i) => ({
        position: i, purpose: s.purpose as "full_look",
        pose: { stance: "relaxed", hands: "natural", weightDistribution: "balanced" },
        expression: { expression: "natural", gaze: "camera", headOrientation: "facing camera", smile: "", brows: "" },
        framing: { camera: "friend_phone" as const, angle: "eye level", crop: "full body" },
      }));
      const newShot = {
        position: session.shots.length, purpose: "full_look" as const,
        pose: { stance: "relaxed", hands: "natural", weightDistribution: "balanced" },
        expression: { expression: "natural", gaze: "camera", headOrientation: "facing camera", smile: "", brows: "" },
        framing: { camera: "friend_phone" as const, angle: "eye level", crop: "full body" },
      };
      const updated = await api.put<Session>(
        `/sessions/${session.id}/plan`,
        { shots: [...existingShots, newShot] },
        { "If-Match": String(session.planRevision) }
      );
      setSession(updated);
      sessionRevRef.current = updated.revision;
      // Collapse all existing shots, leave only the new one (last) expanded
      setCollapsedShots(() => {
        const next: Record<string, boolean> = {};
        for (let i = 0; i < updated.shots.length - 1; i++) {
          next[updated.shots[i]!.id] = true;
        }
        return next;
      });
      // Focus mode: only show the new shot
      const latestShot = updated.shots[updated.shots.length - 1];
      if (latestShot) setFocusNewShotId(latestShot.id);
      const defaultProvider = providers[0]?.id ?? "";
      setPrompts(prev => {
        const next = { ...prev };
        for (const sh of updated.shots) if (!(sh.id in next)) next[sh.id] = buildAutoPrompt(traitsRef.current, formatRef.current);
        return next;
      });
      setShotProviders(prev => {
        const next = { ...prev };
        for (const sh of updated.shots) if (!(sh.id in next)) next[sh.id] = defaultProvider || (updated.providerConnectionId ?? "");
        return next;
      });
      // Re-initialize genState and shotVariants for the new shot IDs using completedByPosition
      // Without this, images disappear because the new shot IDs have no state entries
      setGenState(prev => {
        const next = { ...prev };
        for (const sh of updated.shots) {
          if (sh.id in next) continue; // already set (shouldn't happen, but guard)
          const assetId = sh.selectedAssetId ?? updated.completedByPosition?.[sh.position] ?? null;
          if (assetId) next[sh.id] = { state: "SUCCEEDED", assetId };
        }
        return next;
      });
      // Leave shotVariants undefined for new shots so the genState fallback renders the known asset
      // Re-initialize auto-ref for all shots in the new plan (new shot IDs, so refImages is stale)
      // If a base shot is defined, use its image for all shots; otherwise fall back to the chain
      const currentBaseShotId = baseShotId;
      const baseAssetId = currentBaseShotId
        ? (genState[currentBaseShotId]?.assetId ?? null)
        : null;

      for (let i = 0; i < updated.shots.length; i++) {
        const thisShot = updated.shots[i]!;
        const thisShotId = thisShot.id;

        let refAssetId: string | null = null;
        let refLabel = "";

        if (baseAssetId) {
          // Base shot mode: all non-base shots use the base shot's image
          // Find what position the base shot now occupies (may have changed with new IDs)
          // We identify base by matching assetId across new shots
          const baseNewShot = updated.shots.find(sh => {
            const gs = genState[sh.id];
            return gs?.assetId === baseAssetId || sh.selectedAssetId === baseAssetId;
          });
          if (!baseNewShot || thisShot.id === baseNewShot.id) continue;
          const baseIdx = updated.shots.indexOf(baseNewShot);
          refAssetId = baseAssetId;
          refLabel = `Toma #${baseIdx + 1} (base)`;
        } else {
          // Chain mode: each shot uses the previous shot's image
          if (i === 0) {
            // First shot: use influencer's profile photo if available
            if (influencerRefAssetId && influencerRefUrl) {
              setRefImages(prev => {
                if (prev[thisShotId] && !prev[thisShotId]?.auto) return prev;
                return { ...prev, [thisShotId]: { assetId: influencerRefAssetId, previewUrl: influencerRefUrl, auto: true, autoLabel: "Foto de perfil" } };
              });
            }
            continue;
          }
          const prevShot = updated.shots[i - 1]!;
          refAssetId = prevShot.selectedAssetId ?? updated.completedByPosition?.[i - 1] ?? null;
          refLabel = `Toma #${i}`;
        }

        if (refAssetId) {
          const assetId = refAssetId;
          const label = refLabel;
          batchAssetUrls([assetId]).then(urls => {
            if (!urls[assetId]) return;
            setRefImages(prev => {
              if (prev[thisShotId] && !prev[thisShotId]?.auto) return prev;
              return { ...prev, [thisShotId]: { assetId, previewUrl: urls[assetId]!, auto: true, autoLabel: label } };
            });
          }).catch(() => {});
        }
      }
    } catch (err: unknown) {
      const e = err as { data?: { message?: string } };
      alert(e.data?.message || "Error al añadir toma");
    } finally {
      setAddingShot(false);
    }
  }

  async function uploadReference(shotId: string, file: File) {
    setUploadingRef(prev => ({ ...prev, [shotId]: true }));
    try {
      const arrBuf = await file.arrayBuffer();
      const sha256 = await sha256hex(arrBuf);
      const initRes = await api.post<{ uploadId: string; uploadUrl: string }>(
        "/uploads/init",
        { kind: "image", mime: file.type, bytes: String(file.size) }
      );
      await fetch(initRes.uploadUrl, {
        method: "PUT",
        body: new Blob([arrBuf]),
        headers: { "Content-Type": "application/octet-stream" },
        credentials: "include",
      });
      await api.post(`/uploads/${initRes.uploadId}/complete`, { sha256 });
      const previewUrl = URL.createObjectURL(file);
      setRefImages(prev => ({ ...prev, [shotId]: { assetId: initRes.uploadId, previewUrl } }));
    } catch (err: unknown) {
      const e = err as { data?: { message?: string }; message?: string };
      alert(e.data?.message || e.message || "Error al subir imagen de referencia");
    } finally {
      setUploadingRef(prev => ({ ...prev, [shotId]: false }));
    }
  }

  async function generate(shot: Shot) {
    if (!session) return;
    let prompt = prompts[shot.id] ?? "";
    if (!prompt.trim()) { alert("Escribe una descripción para esta toma"); return; }

    // Multi-character detection: single source of truth for participants, refs, and prompt block
    const _mcMemberIds = shotFamilyMember[shot.id] ?? [];
    const _mcMembersWithRef = _mcMemberIds
      .map(mid => familyMembers.find(m => m.id === mid))
      .filter((m): m is FamilyMember => !!m && !!m.referenceAssetId);
    const _mcInfluencerRef = influencerRefAssetId ?? refImages[shot.id]?.assetId ?? null;
    const isMultiChar = _mcMembersWithRef.length > 0 && !!_mcInfluencerRef;
    let participantsForMultiChar: Array<{ name: string; referenceAssetId: string }> = [];
    if (isMultiChar) {
      participantsForMultiChar = [
        { name: influencerName ?? "Influencer", referenceAssetId: _mcInfluencerRef! },
        ..._mcMembersWithRef.map(m => ({ name: m.name, referenceAssetId: m.referenceAssetId as string })),
      ];
      const refSet = new Set(participantsForMultiChar.map(p => p.referenceAssetId));
      if (refSet.size !== participantsForMultiChar.length) {
        setGenState(prev => ({ ...prev, [shot.id]: { state: "ERROR", error: "Conflicto de identidad: dos personajes comparten la misma imagen de referencia." } }));
        return;
      }
      // Replace single-person REFERENCIA block with per-IMAGE identity assignments matching file order
      const imageLines = participantsForMultiChar
        .map((p, i) => `IMAGE ${i + 1} is ${p.name.toUpperCase()}. Use it exclusively as ${p.name}'s identity reference.`)
        .join('\n');
      const fixedBlock = `REFERENCIA DE IDENTIDAD — BLOQUE FIJO\n${imageLines}\n\nCreate a new photograph showing all ${participantsForMultiChar.length} people together.\nPreserve each person's own facial geometry, skin tone, hair length and body proportions from their assigned reference. Do not blend their identities or duplicate one person's face onto another. The references establish identity only — not clothing, pose, expression or background.`;
      const replaced = prompt.replace(
        /REFERENCIA DE IDENTIDAD[^\n]*\n[\s\S]*?(?=VESTUARIO)/,
        fixedBlock + '\n\n'
      );
      // If the regex didn't match (custom prompt without template structure), prepend the block
      prompt = replaced !== prompt ? replaced : fixedBlock + '\n\n' + prompt;
    }

    // Inject family member descriptions — only in single-person mode (multi-char uses IMAGE N blocks above)
    const selectedMemberIds = _mcMemberIds;
    if (selectedMemberIds.length > 0 && !isMultiChar) {
      const relLabel = (r: string) => r === "partner" ? "pareja" : r === "child" ? "hijo/a" : r === "parent" ? "padre/madre" : r === "sibling" ? "hermano/a" : r === "pet" ? "mascota" : "familiar";
      const memberDescs = selectedMemberIds.map(mid => {
        const member = familyMembers.find(m => m.id === mid);
        if (!member) return null;
        const traits = (() => { try { return JSON.parse(member.traitsJson) as { description?: string }; } catch { return {}; } })();
        return [
          `${member.name} (${relLabel(member.relation)})${member.fictionalAge != null ? `, ${member.fictionalAge} años` : ""}`,
          traits.description ? `Apariencia: ${traits.description}` : "",
          member.notes ? `Notas: ${member.notes}` : "",
          member.linkedInfluencerId ? `(Contenido colaborativo — puede publicarse en ambas cuentas de influencer)` : "",
        ].filter(Boolean).join("\n");
      }).filter(Boolean);
      if (memberDescs.length > 0) {
        const memberNames = selectedMemberIds.map(mid => familyMembers.find(m => m.id === mid)?.name).filter(Boolean);
        const totalPeople = 1 + memberDescs.length; // influencer + each member
        const header = `ACOMPAÑANTES EN LA TOMA (${memberDescs.length})`;
        const body = memberDescs.join("\n\n");
        const closing = `Incluir a ${memberNames.join(" y ")} de forma natural en la escena junto a la influencer.`;
        const restriction = `⚠ RESTRICCIÓN CRÍTICA DE PERSONAS: En esta imagen deben aparecer EXACTAMENTE ${totalPeople} personas. No añadir, inventar ni sustituir ninguna persona adicional. Ignorar cualquier descripción de personas extra que pueda haber en el texto anterior.`;
        prompt = `${prompt}\n\n${header}\n${body}\n${closing}\n\n${restriction}`;
      }
    }

    const selectedProviderId = shotProviders[shot.id] || providers[0]?.id;
    if (!selectedProviderId) {
      setGenState(prev => ({ ...prev, [shot.id]: { state: "ERROR", error: "No hay proveedor de imagen activo. Configura uno en Proveedores." } }));
      return;
    }

    setGenState(prev => ({ ...prev, [shot.id]: { state: "PREPARING" } }));
    const sessionId = session.id;

    try {
      let sessionRevision = sessionRevRef.current;

      // Switch provider if needed for this shot
      if (session.providerConnectionId !== selectedProviderId) {
        await api.put(
          `/sessions/${sessionId}/provider`,
          { providerConnectionId: selectedProviderId, selectionMode: "manual" },
          { "If-Match": String(sessionRevision) }
        );
        const refreshed = await api.get<Session>(`/sessions/${sessionId}`);
        setSession(refreshed);
        sessionRevision = refreshed.revision;
        sessionRevRef.current = sessionRevision;
      }

      // referenceAssetIds: in multi-char mode, ONLY the participant identity images in declaration order.
      // In single-person mode: influencer ref → family member refs → product → prev shot.
      const referenceAssetIds: string[] = [];
      if (isMultiChar) {
        for (const p of participantsForMultiChar) referenceAssetIds.push(p.referenceAssetId);
      } else {
        const refImage = refImages[shot.id];
        if (refImage) referenceAssetIds.push(refImage.assetId);
        for (const memberId of _mcMemberIds) {
          const member = familyMembers.find(m => m.id === memberId);
          if (member?.referenceAssetId && !referenceAssetIds.includes(member.referenceAssetId)) {
            referenceAssetIds.push(member.referenceAssetId);
          }
        }
        const productRefId = productRefs[shot.id];
        if (productRefId && !referenceAssetIds.includes(productRefId)) referenceAssetIds.push(productRefId);
        if (session) {
          const shotIdx = session.shots.findIndex(s => s.id === shot.id);
          if (shotIdx > 0) {
            const prevShot = session.shots[shotIdx - 1];
            const prevAssetId = prevShot
              ? (genState[prevShot.id]?.assetId ?? prevShot.selectedAssetId ?? session.completedByPosition?.[shotIdx - 1] ?? null)
              : null;
            if (prevAssetId && !referenceAssetIds.includes(prevAssetId)) referenceAssetIds.push(prevAssetId);
          }
        }
      }

      setGenState(prev => ({ ...prev, [shot.id]: { state: "QUOTING" } }));
      const quoteRes = await api.post<{ quotes: Array<{ quoteId: string; shotId: string }> }>(
        `/sessions/${sessionId}/quote`,
        { shotIds: [shot.id], prompt, referenceCount: referenceAssetIds.length }
      );
      const quote = quoteRes.quotes.find(q => q.shotId === shot.id);
      if (!quote) throw new Error("No se obtuvo cotización");

      setGenState(prev => ({ ...prev, [shot.id]: { state: "QUEUED" } }));
      const idempotencyKey = `${shot.id}-${Date.now()}`;
      const multiCharExtras: Record<string, unknown> = {};
      if (isMultiChar) {
        multiCharExtras["mode"] = "multi_character_scene";
        multiCharExtras["participants"] = participantsForMultiChar;
      }
      const genRes = await api.post<{ jobId: string }>(
        "/generations",
        { quoteId: quote.quoteId, shotId: shot.id, sessionId, planRevision: shot.planRevision, prompt, referenceAssetIds, ...multiCharExtras },
        { "Idempotency-Key": idempotencyKey }
      );

      const jobId = genRes.jobId;
      setGenState(prev => ({ ...prev, [shot.id]: { state: "QUEUED", jobId } }));

      if (pollingRef.current[shot.id]) clearInterval(pollingRef.current[shot.id]);
      const poll = setInterval(async () => {
        try {
          const variants = await api.get<{ variants: Variant[] }>(`/shots/${shot.id}/variants`);
          const job = variants.variants.find(v => v.jobId === jobId) as (typeof variants.variants[0] & { fallbackNote?: string | null }) | undefined;
          if (!job) return;
          const jobExt = job as typeof job & { moderationRejected?: boolean; moderationProvider?: string; fallbackNote?: string | null };
          type GS = { state: string; jobId?: string; assetId?: string; error?: string; fallbackNote?: string; moderationRejected?: boolean; moderationProvider?: string };
          const noteExtra: Partial<GS> = {};
          if (jobExt.fallbackNote) noteExtra.fallbackNote = jobExt.fallbackNote;
          if (jobExt.moderationRejected) { noteExtra.moderationRejected = true; if (jobExt.moderationProvider) noteExtra.moderationProvider = jobExt.moderationProvider; }
          if (job.state === "SUCCEEDED") {
            clearInterval(poll);
            delete pollingRef.current[shot.id];
            const freshSession = await api.get<Session>(`/sessions/${sessionId}`);
            setSession(freshSession);
            sessionRevRef.current = freshSession.revision;
            const freshShot = freshSession.shots.find(s => s.id === shot.id);
            const assetId = freshShot?.selectedAssetId;
            const gs: GS = assetId ? { state: "SUCCEEDED", jobId, assetId, ...noteExtra } : { state: "SUCCEEDED", jobId, ...noteExtra };
            setGenState(prev => ({ ...prev, [shot.id]: gs }));
            setCollapsedShots(prev => ({ ...prev, [shot.id]: true }));
            void loadVariants(shot.id);
          } else if (job.state === "FAILED") {
            clearInterval(poll);
            delete pollingRef.current[shot.id];
            setGenState(prev => ({ ...prev, [shot.id]: { state: "FAILED", error: "La generación falló", ...noteExtra } as GS }));
          } else {
            setGenState(prev => ({ ...prev, [shot.id]: { state: job.state, jobId, ...noteExtra } as GS }));
          }
        } catch { /* ignore */ }
      }, 4000);
      pollingRef.current[shot.id] = poll;
    } catch (err: unknown) {
      const e = err as { data?: { message?: string }; message?: string };
      setGenState(prev => ({ ...prev, [shot.id]: { state: "ERROR", error: e.data?.message || e.message || "Error al generar" } }));
    }
  }

  async function generateGarmentSwap(shot: Shot) {
    if (!session) return;
    const productRefId = productRefs[shot.id];
    if (!productRefId) { alert("Asigna un producto de referencia primero"); return; }

    const swapPrompt = (garmentSwapPrompts[shot.id] ?? GARMENT_SWAP_TEMPLATE).trim();
    if (!swapPrompt) { alert("Escribe las instrucciones de cambio de prenda"); return; }

    // IMAGEN 1 = foto de identidad real del influencer (no una generación), IMAGEN 2 = producto
    const img1AssetId = influencerRefAssetId ?? refImages[shot.id]?.assetId;
    if (!img1AssetId) { alert("No se encontró la foto de referencia del influencer."); return; }
    const referenceAssetIds: string[] = [img1AssetId, productRefId];

    const selectedProviderId = shotProviders[shot.id] || providers[0]?.id;
    if (!selectedProviderId) {
      setGenState(prev => ({ ...prev, [shot.id]: { state: "ERROR", error: "No hay proveedor de imagen activo." } }));
      return;
    }

    setGenState(prev => ({ ...prev, [shot.id]: { state: "PREPARING" } }));
    const sessionId = session.id;

    try {
      let sessionRevision = sessionRevRef.current;
      if (session.providerConnectionId !== selectedProviderId) {
        await api.put(`/sessions/${sessionId}/provider`, { providerConnectionId: selectedProviderId, selectionMode: "manual" }, { "If-Match": String(sessionRevision) });
        const refreshed = await api.get<Session>(`/sessions/${sessionId}`);
        setSession(refreshed);
        sessionRevision = refreshed.revision;
        sessionRevRef.current = sessionRevision;
      }

      setGenState(prev => ({ ...prev, [shot.id]: { state: "QUOTING" } }));
      const quoteRes = await api.post<{ quotes: Array<{ quoteId: string; shotId: string }> }>(
        `/sessions/${sessionId}/quote`,
        { shotIds: [shot.id], prompt: swapPrompt, referenceCount: referenceAssetIds.length }
      );
      const quote = quoteRes.quotes.find(q => q.shotId === shot.id);
      if (!quote) throw new Error("No se obtuvo cotización");

      setGenState(prev => ({ ...prev, [shot.id]: { state: "QUEUED" } }));
      const idempotencyKey = `${shot.id}-swap-${Date.now()}`;
      const genRes = await api.post<{ jobId: string }>(
        "/generations",
        { quoteId: quote.quoteId, shotId: shot.id, sessionId, planRevision: shot.planRevision, prompt: swapPrompt, referenceAssetIds, editMode: "garment_swap", garmentSwapPrompt: swapPrompt },
        { "Idempotency-Key": idempotencyKey }
      );

      const jobId = genRes.jobId;
      setGenState(prev => ({ ...prev, [shot.id]: { state: "QUEUED", jobId } }));

      if (pollingRef.current[shot.id]) clearInterval(pollingRef.current[shot.id]);
      const poll = setInterval(async () => {
        try {
          const variants = await api.get<{ variants: Array<{ jobId: string; state: string }> }>(`/shots/${shot.id}/variants`);
          const job = variants.variants.find(v => v.jobId === jobId) as (typeof variants.variants[0] & { fallbackNote?: string | null; moderationRejected?: boolean; moderationProvider?: string }) | undefined;
          if (!job) return;
          type GS = { state: string; jobId?: string; assetId?: string; error?: string; fallbackNote?: string; moderationRejected?: boolean; moderationProvider?: string };
          const noteExtra: Partial<GS> = {};
          if (job.fallbackNote) noteExtra.fallbackNote = job.fallbackNote;
          if (job.moderationRejected) { noteExtra.moderationRejected = true; if (job.moderationProvider) noteExtra.moderationProvider = job.moderationProvider; }
          if (job.state === "SUCCEEDED") {
            clearInterval(poll);
            delete pollingRef.current[shot.id];
            const freshSession = await api.get<Session>(`/sessions/${sessionId}`);
            setSession(freshSession);
            sessionRevRef.current = freshSession.revision;
            const freshShot = freshSession.shots.find(s => s.id === shot.id);
            const assetId = freshShot?.selectedAssetId;
            setGenState(prev => ({ ...prev, [shot.id]: assetId ? { state: "SUCCEEDED", jobId, assetId, ...noteExtra } : { state: "SUCCEEDED", jobId, ...noteExtra } }));
            setCollapsedShots(prev => ({ ...prev, [shot.id]: true }));
            void loadVariants(shot.id);
          } else if (job.state === "FAILED") {
            clearInterval(poll);
            delete pollingRef.current[shot.id];
            setGenState(prev => ({ ...prev, [shot.id]: { state: "FAILED", error: "La generación falló", ...noteExtra } as GS }));
          } else {
            setGenState(prev => ({ ...prev, [shot.id]: { state: job.state, jobId, ...noteExtra } as GS }));
          }
        } catch { /* ignore */ }
      }, 4000);
      pollingRef.current[shot.id] = poll;
    } catch (err: unknown) {
      const e = err as { data?: { message?: string }; message?: string };
      setGenState(prev => ({ ...prev, [shot.id]: { state: "ERROR", error: e.data?.message || e.message || "Error al generar" } }));
    }
  }

  const statusColor = (s: string) => ({ draft: "#6366f1", planned: "#8b5cf6", generating: "#d97706", ready: "#2d6a4f", complete: "#2d6a4f", archived: "#9ca3af" }[s] || "#6b7280");
  const genLabel = (s: string) => ({ PREPARING: "Preparando…", QUOTING: "Calculando coste…", QUEUED: "En cola…", SUBMITTING: "Enviando…", RUNNING: "Generando imagen…", PERSISTING: "Guardando…", SUCCEEDED: "Completado ✓", FAILED: "Error", ERROR: "Error" }[s] || s);

  if (loading) return <div style={{ padding: 24, color: "#888" }}>Cargando…</div>;
  if (!session) return <div style={{ padding: 24 }}><Link to="/sessions" style={{ color: "#2d6a4f" }}>← Sesiones</Link><p style={{ color: "#dc2626" }}>Sesión no encontrada</p></div>;

  const activeProviders = providers.filter(p => p.status === "active");

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Link to="/sessions" style={{ color: "#888", fontSize: 13, textDecoration: "none" }}>← Sesiones</Link>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          {editingName ? (
            <form
              onSubmit={async e => {
                e.preventDefault();
                const trimmed = nameInput.trim() || null;
                try {
                  const updated = await api.patch<Session>(`/sessions/${session.id}`, { name: trimmed }, { "If-Match": String(session.revision) });
                  setSession(s => s ? { ...s, name: updated.name ?? null, revision: updated.revision } : s);
                } catch { /* ignore */ }
                setEditingName(false);
              }}
              style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}
            >
              <input
                autoFocus
                value={nameInput}
                onChange={e => setNameInput(e.target.value)}
                placeholder={formatLabel(session.format) ?? "Nombre de la sesión"}
                style={{ fontSize: 18, fontWeight: 700, border: "none", borderBottom: "2px solid #2d6a4f", outline: "none", background: "transparent", padding: "0 2px", minWidth: 200 }}
              />
              <button type="submit" style={{ fontSize: 12, padding: "3px 10px", background: "#2d6a4f", color: "#fff", border: "none", borderRadius: 5, cursor: "pointer" }}>✓</button>
              <button type="button" onClick={() => setEditingName(false)} style={{ fontSize: 12, padding: "3px 8px", background: "none", border: "1px solid #ddd", borderRadius: 5, cursor: "pointer" }}>✕</button>
            </form>
          ) : (
            <h1
              onClick={() => { setNameInput(session.name ?? ""); setEditingName(true); }}
              title="Haz clic para cambiar el nombre"
              style={{ fontSize: 20, fontWeight: 700, margin: "0 0 4px", cursor: "text", display: "inline-flex", alignItems: "center", gap: 8 }}
            >
              {session.name || formatLabel(session.format) || "Sesión"}
              <span style={{ fontSize: 13, color: "#ccc", fontWeight: 400 }}>✏️</span>
            </h1>
          )}
          <div style={{ fontSize: 13, color: "#888" }}>
            {new Date(session.createdAt).toLocaleDateString("es")}
            <span style={{ marginLeft: 8, padding: "1px 8px", borderRadius: 12, background: statusColor(session.status) + "22", color: statusColor(session.status), fontWeight: 600, fontSize: 11 }}>{session.status}</span>
            {session.budgetCapMicroeur && <span style={{ marginLeft: 8 }}>Cap: <strong>€{(Number(session.budgetCapMicroeur) / 1_000_000).toFixed(2)}</strong></span>}
          </div>
        </div>
        <button onClick={addShot} disabled={addingShot} style={{ ...btnPrim, opacity: addingShot ? 0.6 : 1 }}>
          {addingShot ? "Añadiendo…" : "+ Nueva toma"}
        </button>
      </div>

      {activeProviders.length === 0 && (
        <div style={{ background: "#fef9c3", border: "1px solid #fde68a", borderRadius: 8, padding: "12px 16px", marginBottom: 20, fontSize: 13, color: "#92400e" }}>
          Sin proveedores de imagen activos. Añade una API key de Gemini, Flux (BFL) o ChatGPT en la sección Proveedores.
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, margin: "0 0 6px" }}>Tomas ({session.shots.length})</h2>
        <p style={{ margin: 0, fontSize: 13, color: "#666", lineHeight: 1.5 }}>
          Cada toma es una foto diferente de tu influencer. Las tomas posteriores usan automáticamente
          la anterior como referencia de identidad. Puedes marcar la mejor toma generada como{" "}
          <strong>⭐ Base</strong> para que todas las demás la usen como referencia, en lugar del encadenamiento automático.
        </p>
      </div>

      {session.shots.length === 0 ? (
        <div style={{ background: "#fff", borderRadius: 10, padding: 40, textAlign: "center", color: "#888" }}>
          <p style={{ margin: "0 0 12px" }}>Sin tomas. Añade una toma para generar imágenes.</p>
          <button onClick={addShot} disabled={addingShot} style={btnPrim}>+ Añadir primera toma</button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {focusNewShotId && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "#f0fdf4", borderRadius: 8, border: "1px solid #bbf7d0" }}>
              <span style={{ fontSize: 13, color: "#2d6a4f" }}>Nueva toma</span>
              <button
                onClick={() => setFocusNewShotId(null)}
                style={{ marginLeft: "auto", fontSize: 12, padding: "3px 12px", border: "1px solid #2d6a4f", borderRadius: 6, background: "none", color: "#2d6a4f", cursor: "pointer" }}
              >← Ver todas las tomas</button>
            </div>
          )}
          {session.shots.filter(shot => !focusNewShotId || shot.id === focusNewShotId).map((shot, idx) => {
            const realIdx = session.shots.indexOf(shot);
            const gs = genState[shot.id];
            const isGenerating = gs && !["SUCCEEDED", "FAILED", "ERROR"].includes(gs.state);
            const refImg = refImages[shot.id];
            const isUploadingRef = uploadingRef[shot.id] === true;
            // Collect selectable completed images from OTHER shots (for reference picker)
            const otherCompletedImages: Array<{ assetId: string; label: string }> = [];
            if (session) {
              session.shots.forEach((otherShot, otherIdx) => {
                if (otherShot.id === shot.id) return;
                const otherGs = genState[otherShot.id];
                const assetId = otherGs?.assetId ?? otherShot.selectedAssetId ?? session.completedByPosition?.[otherIdx] ?? null;
                if (assetId) otherCompletedImages.push({ assetId, label: `Toma #${otherIdx + 1}` });
              });
            }
            const selectedProviderId = shotProviders[shot.id] || activeProviders[0]?.id || "";
            const selectedProvider = activeProviders.find(p => p.id === selectedProviderId);

            const isBase = baseShotId === shot.id;
            const shotAssetId = gs?.assetId ?? shot.selectedAssetId ?? session.completedByPosition?.[shot.position] ?? null;
            const hasCompletedImage = !!shotAssetId;

            const isCollapsed = !!collapsedShots[shot.id];
            const thumbUrl = shotAssetId ? assetDownloadUrls[shotAssetId] : null;
            // Find the most recent garment swap variant to show its thumbnail in the header
            const swapThumbAssetId = (() => {
              const sv = shotVariants[shot.id];
              if (!sv) return null;
              const swaps = sv.filter(v => v.editMode === "garment_swap" && v.assetId);
              return swaps[swaps.length - 1]?.assetId ?? null;
            })();
            const swapThumbUrl = swapThumbAssetId ? assetDownloadUrls[swapThumbAssetId] : null;

            return (
              <div key={shot.id} style={{ background: "#fff", borderRadius: 10, border: isBase ? "2px solid #d97706" : "2px solid #e5e7eb", overflow: "hidden" }}>
                {/* ── Accordion header ── */}
                <div
                  onClick={() => {
                    const nowCollapsed = !!collapsedShots[shot.id];
                    setCollapsedShots(prev => ({ ...prev, [shot.id]: !prev[shot.id] }));
                    // Load variants on first expand
                    if (nowCollapsed && shot.status === "completed") void loadVariants(shot.id);
                  }}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", cursor: "pointer", userSelect: "none", background: isBase ? "#fffbeb" : "#fff" }}
                >
                  {/* Thumbnail */}
                  {thumbUrl ? (
                    <img
                      src={thumbUrl}
                      alt={`Toma #${realIdx + 1}`}
                      onClick={e => { e.stopPropagation(); setLightboxUrl(thumbUrl); }}
                      title="Ver en grande"
                      style={{ width: 72, height: 96, objectFit: "cover", objectPosition: "top center", borderRadius: 6, flexShrink: 0, border: "1px solid #e5e7eb", cursor: "zoom-in" }}
                    />
                  ) : (
                    <div style={{ width: 72, height: 96, borderRadius: 6, background: "#f3f4f6", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: "#ccc" }}>📷</div>
                  )}

                  {/* Title & badges */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 700, fontSize: 14 }}>Toma #{realIdx + 1}</span>
                      {isBase && <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 10, background: "#fef3c7", color: "#d97706", fontWeight: 700, border: "1px solid #fde68a" }}>⭐ Base</span>}
                      {(() => {
                        const displayStatus = hasCompletedImage ? "completed" : shot.status;
                        const bg = displayStatus === "completed" ? "#d8f3dc" : displayStatus === "failed" ? "#fee2e2" : "#f3f4f6";
                        const color = displayStatus === "completed" ? "#2d6a4f" : displayStatus === "failed" ? "#dc2626" : "#666";
                        return <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 10, background: bg, color, fontWeight: 600 }}>{displayStatus}</span>;
                      })()}
                      {gs && !["SUCCEEDED","FAILED","ERROR"].includes(gs.state) && (
                        <span style={{ fontSize: 10, color: "#2563eb" }}>⏳ {genLabel(gs.state)}</span>
                      )}
                    </div>
                    {shot.purpose && <div style={{ fontSize: 11, color: "#aaa", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{shot.purpose}</div>}
                  </div>

                  {/* Actions (stop click bubbling to accordion) */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                    {hasCompletedImage && thumbUrl && (
                      <button
                        onClick={() => setLightboxUrl(thumbUrl)}
                        title="Ver en grande"
                        style={{ fontSize: 13, padding: "3px 8px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fafafa", color: "#555", cursor: "pointer" }}
                      >🔍</button>
                    )}
                    <button
                      onClick={() => { if (!hasCompletedImage) return; isBase ? setBaseShotId(null) : applyBaseShot(shot.id); }}
                      title={!hasCompletedImage ? "Sin imagen — genera primero" : isBase ? "Quitar como base" : "Usar como toma base"}
                      style={{
                        fontSize: 16, padding: "3px 8px", borderRadius: 8, fontWeight: 600, whiteSpace: "nowrap", transition: "all 0.15s",
                        cursor: hasCompletedImage ? "pointer" : "default",
                        border: isBase ? "1px solid #fde68a" : "1px solid #e5e7eb",
                        background: isBase ? "#fef3c7" : "#fafafa",
                        color: isBase ? "#d97706" : "#ccc",
                        opacity: hasCompletedImage ? 1 : 0.4,
                      }}
                      onMouseEnter={e => { if (hasCompletedImage && !isBase) { const b = e.currentTarget as HTMLButtonElement; b.style.color = "#d97706"; b.style.borderColor = "#fde68a"; } }}
                      onMouseLeave={e => { if (hasCompletedImage && !isBase) { const b = e.currentTarget as HTMLButtonElement; b.style.color = "#ccc"; b.style.borderColor = "#e5e7eb"; } }}
                    >{isBase ? "★" : "☆"}</button>
                    <button
                      onClick={() => deleteShot(shot.id, `Toma #${idx + 1}`)}
                      disabled={deletingShot[shot.id]}
                      title="Eliminar toma"
                      style={{ background: "none", border: "none", cursor: "pointer", padding: "2px 4px", color: "#ccc", fontSize: 15, lineHeight: 1 }}
                      onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = "#e53e3e"; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = "#ccc"; }}
                    >{deletingShot[shot.id] ? "…" : "✕"}</button>
                    <span style={{ fontSize: 16, color: "#bbb", marginLeft: 2 }}>{isCollapsed ? "▸" : "▾"}</span>
                  </div>
                </div>

                {/* ── Accordion body ── */}
                {!isCollapsed && (
                <div style={{ padding: "0 20px 20px", borderTop: "1px solid #f3f4f6" }}><div style={{ height: 16 }} />

                {/* Variants gallery */}
                {(() => {
                  const variants = shotVariants[shot.id]; // undefined = not loaded yet
                  const selectedAssetId = gs?.assetId;
                  if (gs?.state !== "SUCCEEDED" || !selectedAssetId) return null;

                  // Not loaded yet — show placeholder + trigger load
                  if (variants === undefined) {
                    const placeholderUrl = assetDownloadUrls[selectedAssetId];
                    return (
                      <div style={{ marginBottom: 14, position: "relative", display: "inline-block", maxWidth: "100%" }}>
                        {placeholderUrl
                          ? <>
                              <img src={placeholderUrl} alt="Imagen generada" style={{ maxWidth: "100%", maxHeight: 500, borderRadius: 8, display: "block", objectFit: "contain", background: "#f5f5f5" }} />
                              <button onClick={() => setLightboxUrl(placeholderUrl)} title="Ver en grande" style={{ position: "absolute", top: 8, left: 8, background: "rgba(0,0,0,0.55)", border: "none", borderRadius: "50%", width: 30, height: 30, cursor: "pointer", color: "#fff", fontSize: 15, lineHeight: "30px", textAlign: "center", padding: 0 }}>🔍</button>
                            </>
                          : <div style={{ height: 80, display: "flex", alignItems: "center", justifyContent: "center", color: "#999", fontSize: 13 }}>Cargando imagen…</div>
                        }
                      </div>
                    );
                  }

                  // Loaded (even if empty) — show gallery split by type
                  const allVariants = variants.length > 0 ? variants : (selectedAssetId ? [{ jobId: "", assetId: selectedAssetId, state: "SUCCEEDED", createdAt: "", editMode: null }] : []);
                  const creativeVariants = allVariants.filter(v => !v.editMode);
                  const swapVariants = allVariants.filter(v => v.editMode === "garment_swap");

                  const renderVariantGrid = (group: typeof allVariants, accentColor: string) => (
                    <div style={{ display: "grid", gridTemplateColumns: group.length === 1 ? "minmax(0, 360px)" : "repeat(auto-fill, minmax(180px, 1fr))", gap: 10 }}>
                      {group.map(v => {
                        const isSelected = v.assetId === selectedAssetId;
                        const url = v.assetId ? assetDownloadUrls[v.assetId] : null;
                        return (
                          <div
                            key={v.jobId || v.assetId}
                            onClick={() => { if (v.assetId && v.jobId && !isSelected && !selectingVariant[shot.id]) void selectVariant(shot.id, v.jobId, v.assetId); }}
                            style={{
                              position: "relative", borderRadius: 8, overflow: "hidden",
                              border: `3px solid ${isSelected ? accentColor : "#e5e7eb"}`,
                              cursor: (v.jobId && !isSelected) ? "pointer" : "default",
                              background: "#f5f5f5",
                            }}
                          >
                            {url
                              ? <img src={url} alt="Variante" style={{ width: "100%", display: "block", objectFit: "contain", aspectRatio: "9/16", background: "#f5f5f5" }} />
                              : <div style={{ aspectRatio: "9/16", display: "flex", alignItems: "center", justifyContent: "center", color: "#aaa", fontSize: 12 }}>Cargando…</div>
                            }
                            {url && (
                              <button
                                onClick={e => { e.stopPropagation(); setLightboxUrl(url); }}
                                title="Ver en grande"
                                style={{ position: "absolute", top: 5, left: 5, background: "rgba(0,0,0,0.55)", border: "none", borderRadius: "50%", width: 26, height: 26, cursor: "pointer", color: "#fff", fontSize: 14, lineHeight: "26px", textAlign: "center", padding: 0, zIndex: 1 }}
                              >🔍</button>
                            )}
                            {isSelected && (
                              <div style={{ position: "absolute", top: 6, right: 6, background: accentColor, color: "#fff", borderRadius: 12, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>
                                ✓ Seleccionada
                              </div>
                            )}
                            {!isSelected && v.jobId && (
                              <>
                                <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(0,0,0,0.45)", color: "#fff", fontSize: 11, padding: "4px 8px", textAlign: "center" }}>
                                  {selectingVariant[shot.id] ? "…" : "Usar esta"}
                                </div>
                                <button
                                  onClick={e => { e.stopPropagation(); if (!deletingVariant[v.jobId!]) void deleteVariant(shot.id, v.jobId!, v.assetId); }}
                                  title="Eliminar esta versión"
                                  style={{ position: "absolute", top: 5, right: 5, background: "rgba(0,0,0,0.55)", border: "none", borderRadius: "50%", width: 22, height: 22, cursor: "pointer", color: "#fff", fontSize: 13, lineHeight: "22px", textAlign: "center", padding: 0 }}
                                >
                                  {deletingVariant[v.jobId!] ? "…" : "✕"}
                                </button>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );

                  if (allVariants.length > 0) {
                    return (
                      <div style={{ marginBottom: 14 }}>
                        {/* Creative photos */}
                        {creativeVariants.length > 0 && (
                          <div style={{ marginBottom: swapVariants.length > 0 ? 16 : 0 }}>
                            {(creativeVariants.length > 1 || swapVariants.length > 0) && (
                              <div style={{ fontSize: 11, fontWeight: 600, color: "#2d6a4f", marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
                                ✨ Foto creativa {creativeVariants.length > 1 ? `(${creativeVariants.length})` : ""}
                                <button onClick={() => void loadVariants(shot.id)} style={{ fontSize: 10, background: "none", border: "1px solid #d1d5db", borderRadius: 5, padding: "1px 5px", cursor: "pointer", color: "#888", fontWeight: 400 }}>↺</button>
                              </div>
                            )}
                            {renderVariantGrid(creativeVariants, "#2d6a4f")}
                          </div>
                        )}
                        {/* Garment swap photos */}
                        {swapVariants.length > 0 && (
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: "#7c3aed", marginBottom: 6 }}>
                              🔄 Cambio de prenda {swapVariants.length > 1 ? `(${swapVariants.length})` : ""}
                            </div>
                            {renderVariantGrid(swapVariants, "#7c3aed")}
                          </div>
                        )}
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Moderation rejection notice */}
                {gs?.moderationRejected && (
                  <div style={{ marginBottom: 8, padding: "8px 12px", borderRadius: 6, fontSize: 12, background: "#fffbeb", color: "#92400e", border: "1px solid #fcd34d" }}>
                    <div style={{ fontWeight: 600, marginBottom: 3 }}>⚠ Rechazado por {gs.moderationProvider ?? "el proveedor"} por restricciones de contenido</div>
                    <div>Pulsa <strong>✨ Generar</strong> para que la IA corrija automáticamente las partes problemáticas del prompt e intenta de nuevo.</div>
                  </div>
                )}
                {/* Fallback provider notice (non-moderation) */}
                {gs?.fallbackNote && !gs.moderationRejected && (
                  <div style={{ marginBottom: 8, padding: "7px 12px", borderRadius: 6, fontSize: 12, background: "#fffbeb", color: "#92400e", border: "1px solid #fcd34d", display: "flex", alignItems: "flex-start", gap: 6 }}>
                    <span style={{ flexShrink: 0 }}>⚠</span>
                    <span>{gs.fallbackNote}</span>
                  </div>
                )}

                {/* Generation state message */}
                {gs && gs.state !== "SUCCEEDED" && (
                  <div style={{
                    marginBottom: 12, padding: "8px 12px", borderRadius: 6, fontSize: 13,
                    background: ["ERROR", "FAILED"].includes(gs.state) ? "#fef2f2" : "#eff6ff",
                    color: ["ERROR", "FAILED"].includes(gs.state) ? "#dc2626" : "#1d4ed8",
                  }}>
                    {isGenerating && "⏳ "}{genLabel(gs.state)}{gs.error ? `: ${gs.error}` : ""}
                  </div>
                )}

                {/* Provider selector */}
                {activeProviders.length > 1 && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: "#666", marginBottom: 4, fontWeight: 500 }}>IA generadora</div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {activeProviders.map(p => (
                        <button
                          key={p.id}
                          onClick={() => setShotProviders(prev => ({ ...prev, [shot.id]: p.id }))}
                          disabled={isGenerating}
                          style={{
                            padding: "5px 12px", borderRadius: 20, fontSize: 12, cursor: "pointer", border: "2px solid",
                            borderColor: selectedProviderId === p.id ? "#2d6a4f" : "#e5e7eb",
                            background: selectedProviderId === p.id ? "#f0fdf4" : "#fafafa",
                            color: selectedProviderId === p.id ? "#2d6a4f" : "#555",
                            fontWeight: selectedProviderId === p.id ? 600 : 400,
                          }}
                        >
                          {providerLabel(p)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {activeProviders.length === 1 && selectedProvider && (
                  <div style={{ marginBottom: 10, fontSize: 12, color: "#888" }}>
                    Proveedor: <strong style={{ color: "#2d6a4f" }}>{providerLabel(selectedProvider)}</strong>
                  </div>
                )}

                {/* Reference image */}
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 6, fontWeight: 500 }}>
                    Foto de referencia{" "}
                    <span style={{ color: "#aaa", fontWeight: 400 }}>
                      (influencer principal{(shotFamilyMember[shot.id]?.length ?? 0) > 0 ? " + familiares seleccionados" : ""})
                    </span>
                  </div>
                  {/* Show family member reference photos that will be sent automatically */}
                  {(shotFamilyMember[shot.id] ?? []).some(mid => familyMembers.find(m => m.id === mid)?.referenceAssetId) && (
                    <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
                      {(shotFamilyMember[shot.id] ?? []).map(mid => {
                        const member = familyMembers.find(m => m.id === mid);
                        if (!member?.referenceAssetId) return null;
                        const url = assetDownloadUrls[member.referenceAssetId];
                        if (!url) {
                          batchAssetUrls([member.referenceAssetId]).then(urls => {
                            if (urls[member.referenceAssetId!]) setAssetDownloadUrls(prev => ({ ...prev, [member.referenceAssetId!]: urls[member.referenceAssetId!]! }));
                          }).catch(() => {});
                          return null;
                        }
                        return (
                          <div key={mid} style={{ position: "relative", textAlign: "center" }}>
                            <img src={url} alt={member.name} style={{ width: 48, height: 48, objectFit: "cover", objectPosition: "top center", borderRadius: 6, border: "2px solid #7c3aed", display: "block" }} />
                            <div style={{ fontSize: 9, color: "#7c3aed", marginTop: 2, maxWidth: 48, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{member.name}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                    {refImg && (
                      <div style={{ position: "relative" }}>
                        <img src={refImg.previewUrl} alt="Referencia" style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 6, border: `2px solid ${refImg.auto ? "#2563eb" : "#2d6a4f"}` }} />
                        {refImg.auto && (
                          <div style={{ position: "absolute", bottom: -2, left: 0, right: 0, background: "rgba(37,99,235,0.85)", color: "#fff", fontSize: 9, textAlign: "center", borderRadius: "0 0 4px 4px", padding: "1px 2px", lineHeight: 1.4 }}>
                            {refImg.autoLabel}
                          </div>
                        )}
                        <button
                          onClick={() => setRefImages(prev => ({ ...prev, [shot.id]: null }))}
                          style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%", background: "#dc2626", color: "#fff", border: "none", cursor: "pointer", fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center" }}
                        >×</button>
                      </div>
                    )}
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {refImg?.auto && (
                        <div style={{ fontSize: 11, color: "#2563eb", fontWeight: 500 }}>
                          Usando imagen de {refImg.autoLabel} automáticamente
                        </div>
                      )}
                      <label style={{ cursor: "pointer" }}>
                        <div style={{ padding: "6px 12px", border: "1px dashed #ccc", borderRadius: 6, fontSize: 12, color: isUploadingRef ? "#aaa" : "#444", background: "#fafafa", textAlign: "center" }}>
                          {isUploadingRef ? "Subiendo…" : refImg ? "Cambiar foto" : "+ Subir foto de referencia"}
                        </div>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          style={{ display: "none" }}
                          disabled={isUploadingRef || isGenerating}
                          onChange={e => { const f = e.target.files?.[0]; if (f) void uploadReference(shot.id, f); }}
                        />
                      </label>
                    </div>
                  </div>
                  {/* Picker: influencer profile reference + product ref + previously generated shots */}
                  {(influencerRefUrl || productRefs[shot.id] || otherCompletedImages.length > 0) && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 11, color: "#888", marginBottom: 6 }}>O selecciona de tus fotos generadas:</div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {/* Influencer profile reference photo */}
                        {influencerRefUrl && influencerRefAssetId && (() => {
                          const isSelected = refImg?.assetId === influencerRefAssetId;
                          return (
                            <button
                              key="profile-ref"
                              onClick={() => setRefImages(prev => ({ ...prev, [shot.id]: { assetId: influencerRefAssetId, previewUrl: influencerRefUrl, auto: false } }))}
                              title="Usar foto de perfil del influencer como referencia"
                              style={{ padding: 0, border: `2px solid ${isSelected ? "#d97706" : "#f59e0b"}`, borderRadius: 6, cursor: "pointer", background: "none", position: "relative" }}
                            >
                              <img src={influencerRefUrl} alt="Foto de perfil" style={{ width: 52, height: 52, objectFit: "cover", objectPosition: "top center", borderRadius: 4, display: "block" }} />
                              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(217,119,6,0.85)", color: "#fff", fontSize: 9, textAlign: "center", borderRadius: "0 0 3px 3px", padding: "1px 2px" }}>Foto perfil</div>
                              {isSelected && <div style={{ position: "absolute", top: 2, right: 2, background: "#d97706", borderRadius: "50%", width: 14, height: 14, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "#fff" }}>✓</div>}
                            </button>
                          );
                        })()}
                        {/* Catalog product reference (outfit image — always included as secondary ref) */}
                        {productRefs[shot.id] && (() => {
                          const productAssetId = productRefs[shot.id]!;
                          const productUrl = assetDownloadUrls[productAssetId];
                          return (
                            <div key="product-ref" style={{ position: "relative" }} title="Imagen del producto del catálogo — incluida como referencia de outfit">
                              {productUrl ? (
                                <img src={productUrl} alt="Producto" style={{ width: 52, height: 52, objectFit: "cover", borderRadius: 4, display: "block", border: "2px solid #7c3aed" }} />
                              ) : (
                                <div style={{ width: 52, height: 52, borderRadius: 4, background: "#ede9fe", border: "2px solid #7c3aed", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>👗</div>
                              )}
                              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(124,58,237,0.85)", color: "#fff", fontSize: 9, textAlign: "center", borderRadius: "0 0 3px 3px", padding: "1px 2px" }}>Producto</div>
                              <div style={{ position: "absolute", top: 2, right: 2, background: "#7c3aed", borderRadius: "50%", width: 14, height: 14, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "#fff" }}>✓</div>
                              <button
                                onClick={() => setProductRefs(prev => { const n = { ...prev }; delete n[shot.id]; return n; })}
                                style={{ position: "absolute", top: -6, left: -6, width: 16, height: 16, borderRadius: "50%", background: "#dc2626", color: "#fff", border: "none", cursor: "pointer", fontSize: 9, display: "flex", alignItems: "center", justifyContent: "center" }}
                                title="Quitar referencia de producto"
                              >×</button>
                            </div>
                          );
                        })()}
                        {otherCompletedImages.map(({ assetId, label }) => {
                          const url = assetDownloadUrls[assetId];
                          const isSelected = refImg?.assetId === assetId;
                          if (!url) return null;
                          return (
                            <button
                              key={assetId}
                              onClick={() => setRefImages(prev => ({ ...prev, [shot.id]: { assetId, previewUrl: url, auto: false } }))}
                              title={`Usar ${label} como referencia`}
                              style={{ padding: 0, border: `2px solid ${isSelected ? "#2d6a4f" : "#ddd"}`, borderRadius: 6, cursor: "pointer", background: "none", position: "relative" }}
                            >
                              <img src={url} alt={label} style={{ width: 52, height: 52, objectFit: "cover", borderRadius: 4, display: "block" }} />
                              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(0,0,0,0.55)", color: "#fff", fontSize: 9, textAlign: "center", borderRadius: "0 0 3px 3px", padding: "1px 2px" }}>{label}</div>
                              {isSelected && <div style={{ position: "absolute", top: 2, right: 2, background: "#2d6a4f", borderRadius: "50%", width: 14, height: 14, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "#fff" }}>✓</div>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Family member picker */}
                {familyMembers.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: "#666", marginBottom: 6, fontWeight: 500 }}>
                      Incluir miembro de la familia en la toma{" "}
                      <span style={{ color: "#aaa", fontWeight: 400 }}>(opcional — su descripción se añade al prompt)</span>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        onClick={() => {
                          setShotFamilyMember(prev => ({ ...prev, [shot.id]: [] }));
                          // Restore influencer's own reference photo when clearing all family members
                          setRefImages(prev => {
                            const cur = prev[shot.id];
                            if (cur && !cur.auto) return prev;
                            if (!influencerRefAssetId || !influencerRefUrl) return prev;
                            return { ...prev, [shot.id]: { assetId: influencerRefAssetId, previewUrl: influencerRefUrl, auto: true, autoLabel: "Foto de perfil" } };
                          });
                        }}
                        style={{
                          padding: "5px 12px", borderRadius: 20, fontSize: 12, cursor: "pointer", border: "2px solid",
                          borderColor: !(shotFamilyMember[shot.id]?.length) ? "#2d6a4f" : "#e5e7eb",
                          background: !(shotFamilyMember[shot.id]?.length) ? "#f0fdf4" : "#fafafa",
                          color: !(shotFamilyMember[shot.id]?.length) ? "#2d6a4f" : "#555",
                          fontWeight: !(shotFamilyMember[shot.id]?.length) ? 600 : 400,
                        }}
                      >
                        Solo ella
                      </button>
                      {familyMembers.map(m => {
                        const selected = (shotFamilyMember[shot.id] ?? []).includes(m.id);
                        const emoji = m.relation === "pet" ? "🐾" : m.relation === "child" ? "👶" : m.relation === "partner" ? "💑" : "👤";
                        return (
                          <button
                            key={m.id}
                            onClick={() => {
                              const current = shotFamilyMember[shot.id] ?? [];
                              const isSelected = current.includes(m.id);
                              const next = isSelected ? current.filter(id => id !== m.id) : [...current, m.id];
                              setShotFamilyMember(prev => ({ ...prev, [shot.id]: next }));
                              // Eagerly cache the family member's reference asset URL if not loaded yet
                              if (!isSelected && m.referenceAssetId && !assetDownloadUrls[m.referenceAssetId]) {
                                batchAssetUrls([m.referenceAssetId]).then(urls => {
                                  if (urls[m.referenceAssetId!]) setAssetDownloadUrls(prev => ({ ...prev, [m.referenceAssetId!]: urls[m.referenceAssetId!]! }));
                                }).catch(() => {});
                              }
                            }}
                            style={{
                              padding: "5px 12px", borderRadius: 20, fontSize: 12, cursor: "pointer", border: "2px solid",
                              borderColor: selected ? "#7c3aed" : "#e5e7eb",
                              background: selected ? "#f5f3ff" : "#fafafa",
                              color: selected ? "#7c3aed" : "#555",
                              fontWeight: selected ? 600 : 400,
                            }}
                            title={m.linkedInfluencerId ? "Influencer vinculado — el contenido puede publicarse en ambas cuentas" : undefined}
                          >
                            {emoji} {m.name}{m.linkedInfluencerId ? " 🔗" : ""}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Product catalog picker */}
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 6, fontWeight: 500 }}>
                    Producto del catálogo{" "}
                    <span style={{ color: "#aaa", fontWeight: 400 }}>(opcional — referencia de outfit o garment swap)</span>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <button
                      onClick={() => setProductRefs(prev => { const n = { ...prev }; delete n[shot.id]; return n; })}
                      style={{
                        padding: "5px 12px", borderRadius: 20, fontSize: 12, cursor: "pointer", border: "2px solid",
                        borderColor: !productRefs[shot.id] ? "#2d6a4f" : "#e5e7eb",
                        background: !productRefs[shot.id] ? "#f0fdf4" : "#fafafa",
                        color: !productRefs[shot.id] ? "#2d6a4f" : "#555",
                        fontWeight: !productRefs[shot.id] ? 600 : 400,
                      }}
                    >Sin producto</button>
                    {catalogProducts.length === 0 ? (
                      <span style={{ fontSize: 12, color: "#bbb" }}>Sin productos en el catálogo</span>
                    ) : (
                      catalogProducts.map(p => {
                        const img = catalogProductImages[p.id];
                        const isSelected = !!img && productRefs[shot.id] === img.assetId;
                        return (
                          <button
                            key={p.id}
                            onClick={() => { if (img) setProductRefs(prev => ({ ...prev, [shot.id]: img.assetId })); }}
                            disabled={!img}
                            style={{
                              padding: "4px 10px", borderRadius: 20, fontSize: 12,
                              cursor: img ? "pointer" : "default", border: "2px solid",
                              borderColor: isSelected ? "#7c3aed" : "#e5e7eb",
                              background: isSelected ? "#f5f3ff" : "#fafafa",
                              color: isSelected ? "#7c3aed" : img ? "#555" : "#bbb",
                              fontWeight: isSelected ? 600 : 400,
                              display: "flex", alignItems: "center", gap: 5,
                            }}
                            title={p.title}
                          >
                            {img ? (
                              <img src={img.url} alt="" style={{ width: 16, height: 16, borderRadius: 2, objectFit: "cover", flexShrink: 0 }} />
                            ) : <span style={{ fontSize: 11 }}>👗</span>}
                            <span style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</span>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Prompt generator + generate button — always visible */}
                <>
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 4, fontWeight: 500 }}>
                    Generador de descripción con IA
                  </div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <textarea
                      value={promptBriefs[shot.id] ?? ""}
                      onChange={e => setPromptBriefs(prev => ({ ...prev, [shot.id]: e.target.value }))}
                      rows={2}
                      placeholder="Describe brevemente la foto: outfit, escenario, pose, ambiente… (ej: mujer en cocina moderna, outfit casual verano, preparando café)"
                      style={{ ...inp, resize: "vertical", fontSize: 12, flex: 1 }}
                      disabled={isGenerating || generatingPrompt[shot.id]}
                    />
                    <button
                      onClick={() => void generatePrompt(shot.id)}
                      disabled={!promptBriefs[shot.id]?.trim() || isGenerating || generatingPrompt[shot.id]}
                      style={{ ...btnPrim, background: "#2563eb", whiteSpace: "nowrap", alignSelf: "flex-end", opacity: (!promptBriefs[shot.id]?.trim() || isGenerating || generatingPrompt[shot.id]) ? 0.5 : 1 }}
                    >
                      {generatingPrompt[shot.id] ? "Generando…" : "✨ Generar"}
                    </button>
                  </div>

                  <div style={{ fontSize: 12, color: "#666", marginBottom: 4, fontWeight: 500, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span>Prompt final</span>
                    <span style={{ color: "#aaa", fontWeight: 400 }}>(edítalo si quieres antes de generar)</span>
                    {shot.lastModel && (
                      <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 10, background: "#f0f9ff", color: "#0369a1", border: "1px solid #bae6fd", fontWeight: 500 }}>
                        último: {shot.lastModel}
                      </span>
                    )}
                    {shot.lastPrompt && (
                      <button
                        onClick={() => setPrompts(prev => ({ ...prev, [shot.id]: shot.lastPrompt! }))}
                        style={{ fontSize: 10, padding: "1px 8px", borderRadius: 10, background: "#f0fdf4", color: "#2d6a4f", border: "1px solid #bbf7d0", cursor: "pointer", fontWeight: 500 }}
                        title="Restaurar el prompt que se usó en la última generación"
                      >↩ Restaurar último</button>
                    )}
                  </div>
                  <textarea
                    value={prompts[shot.id] ?? ""}
                    onChange={e => setPrompts(prev => ({ ...prev, [shot.id]: e.target.value }))}
                    rows={12}
                    style={{ ...inp, resize: "vertical", fontSize: 12, minHeight: 200 }}
                    disabled={isGenerating}
                  />
                </div>

                <button
                  onClick={() => void generate(shot)}
                  disabled={isGenerating || activeProviders.length === 0}
                  style={{ ...btnPrim, opacity: (isGenerating || activeProviders.length === 0) ? 0.5 : 1 }}
                >
                  {isGenerating ? "Generando…" : gs?.state === "SUCCEEDED" ? "Regenerar" : "Generar imagen"}
                </button>
                </>

                {/* Garment swap section — single call: identity ref photo + product → edited result */}
                {productRefs[shot.id] && (
                  <div style={{ marginTop: 20, paddingTop: 16, borderTop: "2px solid #ede9fe" }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "#7c3aed", marginBottom: 4 }}>🔄 Cambiar prenda</div>
                    <div style={{ fontSize: 12, color: "#888", marginBottom: 10 }}>
                      Una sola llamada: foto de referencia del influencer (IMAGEN 1) + foto del producto (IMAGEN 2).
                      Sin generación intermedia. Cambia la prenda, varía ligeramente pose y expresión, conserva identidad y escena.
                    </div>
                    <div style={{ fontSize: 11, color: "#7c3aed", marginBottom: 6, fontWeight: 500 }}>
                      Instrucciones de edición (edita si necesitas ajustar detalles del vestido o escena)
                    </div>
                    <textarea
                      value={garmentSwapPrompts[shot.id] ?? GARMENT_SWAP_TEMPLATE}
                      onChange={e => setGarmentSwapPrompts(prev => ({ ...prev, [shot.id]: e.target.value }))}
                      rows={10}
                      style={{ ...inp, resize: "vertical", fontSize: 12, borderColor: "#c4b5fd", marginBottom: 10 }}
                      disabled={isGenerating}
                    />
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <button
                        onClick={() => void generateGarmentSwap(shot)}
                        disabled={isGenerating || activeProviders.length === 0}
                        style={{ ...btnPrim, background: "#7c3aed", opacity: (isGenerating || activeProviders.length === 0) ? 0.5 : 1 }}
                      >
                        {isGenerating ? "Generando…" : "🔄 Cambiar prenda"}
                      </button>
                      <button
                        onClick={() => setGarmentSwapPrompts(prev => ({ ...prev, [shot.id]: GARMENT_SWAP_TEMPLATE }))}
                        style={{ fontSize: 11, padding: "5px 10px", border: "1px solid #c4b5fd", borderRadius: 6, background: "none", color: "#7c3aed", cursor: "pointer" }}
                      >↺ Restaurar prompt</button>
                    </div>
                  </div>
                )}

                {shot.diversityWarning && <div style={{ marginTop: 8, fontSize: 11, color: "#d97706" }}>⚠ {shot.diversityWarning}</div>}
                </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          onClick={() => setLightboxUrl(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.88)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", cursor: "zoom-out" }}
        >
          <img
            src={lightboxUrl}
            alt="Vista ampliada"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain", borderRadius: 8, boxShadow: "0 8px 40px rgba(0,0,0,0.6)", cursor: "default" }}
          />
          <button
            onClick={() => setLightboxUrl(null)}
            style={{ position: "fixed", top: 20, right: 24, background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%", width: 40, height: 40, cursor: "pointer", color: "#fff", fontSize: 20, lineHeight: "40px", textAlign: "center", padding: 0 }}
          >✕</button>
        </div>
      )}
    </div>
  );
}

const GARMENT_SWAP_TEMPLATE = `Edit image 1, using image 2 exclusively as the exact dress product reference.

IDENTITY — fixed, do not alter:
Same clearly adult approximately 30 year old brunette woman from image 1. Preserve her facial structure and proportions, characteristic shape of eyes, nose, lips and jaw, age, skin tone, freckles, hair texture and length, body proportions. Same cream shoulder bag, gold jewelry and tan flat sandals. Preserving identity does NOT mean preserving the exact position of every facial element — expressions naturally move soft tissue without changing who the person is.

EXPRESSION AND POSE — change these:
New candid expression: spontaneous moderate smile with lips slightly parted and a small portion of upper teeth visible. Cheeks gently lifted, eyelids naturally narrowed by the smile, eyes looking toward the camera. Head upright WITHOUT the lateral tilt from image 1, face rotated about 15 degrees toward picture left while eyes look back at the photographer. This is another instant of the same person — allow the expression to naturally modify lips, cheeks, eyelids and brows without altering facial identity.
Pose: torso turned slight three-quarter, relaxed weight shift with one foot slightly forward. Hand by bag gently holds the strap higher near the collarbone, opposite arm hanging loosely with relaxed fingers. Plausible hands and joints, natural pose rather than fashion contortion.

GARMENT — replace exactly:
Replace her dress with the EXACT dress in image 2: subtly heathered matte jersey, short set-in sleeves, V neckline, twisted central knot and radiating gathered panels below bust, curved empire waist seam with gathering below, softly flowing A-line skirt ending ABOVE knees. Preserve product color, material, cut, construction and length exactly. No redesign, belt, slit or buttons. Keep the entire dress front and knot unobstructed. Do not transfer product model, straw hat, straw bag or white backdrop.

SCENE — preserve:
Same restaurant doorway, pavement, planters and ordinary background life from image 1.

TECHNICAL:
Full body vertical photo, shoes visible, casual high-end phone snapshot with slight framing imperfection, natural uneven daylight, skin pores and loose strands of hair, believable fabric creases. No airbrushing, cinematic lighting, text or watermark.`;

function formatLabel(format?: string): string {
  if (!format) return "Sesión";
  return { portrait_9_16: "Vertical 9:16 (Reels/TikTok)", square_1_1: "Cuadrado 1:1 (Feed)", landscape_16_9: "Horizontal 16:9" }[format] ?? format;
}

const btnPrim: React.CSSProperties = { padding: "8px 18px", background: "#2d6a4f", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 };
const inp: React.CSSProperties = { padding: "8px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 14, outline: "none", width: "100%", boxSizing: "border-box" };
